import { useEffect, useState } from "react";
import { useTrans } from "../../../hooks";
import { useAuthData } from "../../../contexts";
import { useParams } from "react-router-dom";
import {
    Box,
    Button,
    Divider,
    HStack,
    Input,
    Select,
    useDisclosure,
    useToast,
    VStack,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import {
    AppAlertDelete,
    AppDatePicker,
    AppDateSelectSlider,
    AppDateSelectSliderDataType,
    AppFormControl,
    AppIcon,
    AppLoader,
    AppPageContent,
    AppPageHeader,
    AppSelectOption,
} from "../../../components";
import {
    Internship,
    InternshipSchedule,
    InternshipScheduleSeat,
    InternshipScheduleSeatApi,
    Timesheet,
    TimesheetApi,
    TimesheetType,
    TimesheetTypeApi,
    TimeSlots,
    UserApi,
} from "../../../apis";
import {
    errorsDataType,
    errorsMessageDataType,
} from "../../../apis/entities/dtos";
import { APPC, cPath, UTIL } from "../../../config";
import { UnprocessableErrorResponse } from "../../../services/ApiPlatform/models";

export const TimesheetPage = () => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const { internshipScheduleSeatId } = useParams();
    const { user } = useAuthData();
    const deleteAlertState = useDisclosure();

    // state & const
    const dayjs = UTIL.getDayJs();
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [editId, setEditId] = useState(0);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<InternshipSchedule>(
        new InternshipSchedule()
    );
    const [internshipScheduleSeat, setInternshipScheduleSeat] =
        useState<InternshipScheduleSeat>();
    const [timesheet, setTimesheet] = useState<Timesheet[]>([]);
    const [dateSliderData, setDateSliderData] = useState<
        AppDateSelectSliderDataType[]
    >([]);
    const [selectedDate, setSelectedDate] = useState<Date>();
    const [isSelectedDateDisabled, setIsSelectedDateDisabled] = useState(false);
    const [selectedStartTime, setSelectedStartTime] = useState<Date | null>();
    const [selectedEndTime, setSelectedEndTime] = useState<Date | null>();
    const [selectedTimesheetType, setSelectedTimesheetType] =
        useState<string>();
    const [timesheetTypeOptions, setTimesheetTypeOptions] =
        useState<AppSelectOption[]>();
    const [selectedComment, setSelectedComment] = useState<string>();
    const [errors, setErrors] = useState<errorsDataType>({
        timesheetType: false,
        startTime: false,
        endTime: false,
    });
    const [errorMessages, setErrorMessages] = useState<errorsMessageDataType>(
        {}
    );
    const [actionSelectId, setActionSelectId] = useState(0);

    // handler
    const calculateDateSliderData = (is: InternshipSchedule) => {
        const weekDays = is?.weekDays;
        const weekStart = dayjs(selectedDate)
            .subtract(dayjs(selectedDate).isoWeekday() - 1, "days")
            .set("hour", 0)
            .set("minute", 0)
            .set("second", 0);
        const wd: string[] = [];
        if (is.isScheduleDetail) {
            const cts = (is.timeSlots as TimeSlots).filter(
                (ts) => dayjs(`${ts[0].date} 00:00:00+00:00`) >= weekStart
            );
            cts[0].forEach((t) => {
                if (t.isChecked) {
                    wd.push(t.date as string);
                }
            });
        }

        const dsd: AppDateSelectSliderDataType[] = [];
        const startDate = dayjs(is?.startDate)
            .set("hour", 0)
            .set("minute", 0)
            .set("second", 0);
        const endDate = dayjs(is?.endDate)
            .set("hour", 0)
            .set("minute", 0)
            .set("second", 0);
        for (let i = 0; i < 7; i++) {
            const dt = dayjs(weekStart).add(i, "days");
            const isDisabled = is.isScheduleDetail
                ? !wd.includes(dt.format(APPC.APP_HTML_DATE_FORMAT))
                : !weekDays[UTIL.getWeekDayName(dt.format("d"))] ||
                  dt < startDate ||
                  dt > endDate;
            dsd.push({
                date: dt.toDate(),
                isDisabled: isDisabled,
            });
            if (
                dayjs(selectedDate).format(APPC.APP_HTML_DATE_FORMAT) ===
                dayjs(dt).format(APPC.APP_HTML_DATE_FORMAT)
            ) {
                setIsSelectedDateDisabled(isDisabled);
            }
        }
        setDateSliderData(dsd);
        resetFormState();
    };

    const fetchTimesheetTypeData = async () => {
        await TimesheetTypeApi.getCollection<TimesheetType>(1, {
            pagination: false,
        }).then(({ errorMessage, response }) => {
            if (errorMessage) {
                toast({
                    title: errorMessage,
                    status: "error",
                });
            } else if (response !== null) {
                setTimesheetTypeOptions(
                    response.items.map((t) => {
                        return { value: t.id, label: t.name };
                    })
                );
            }
        });
    };

    const fetchTimesheetData = async (sd: Date) => {
        const params = {
            "internshipSchedule.id": data.id,
            date: dayjs(sd).format(APPC.APP_HTML_DATE_FORMAT),
            "order[startTime]": "ASC",
        };
        await TimesheetApi.getCollectionForStudent<Timesheet>(1, params).then(
            ({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    setTimesheet(response.items);
                }
            }
        );
    };

    const onTimesheetSubmitHandler = () => {
        if (!validate()) {
            return;
        }
        const timesheet = new Timesheet({
            date: dayjs(selectedDate).format(APPC.APP_HTML_DATE_FORMAT),
            startTime: `1970-01-01T${dayjs(selectedStartTime).format(APPC.APP_HTML_TIME_FORMAT)}+00:00`,
            endTime: `1970-01-01T${dayjs(selectedEndTime).format(APPC.APP_HTML_TIME_FORMAT)}+00:00`,
            timesheetType: TimesheetTypeApi.toResourceIRI(
                selectedTimesheetType
            ),
            internshipSchedule: data?.["@id"],
            comment: selectedComment || "",
            user: UserApi.toResourceIRI(user?.id),
        });
        setLoading(true);
        TimesheetApi.createOrUpdate<Timesheet, Timesheet>(editId, timesheet)
            .then(({ response, error, errorMessage }) => {
                if (error instanceof UnprocessableErrorResponse) {
                    const { violations } = error;
                    Object.keys(violations).map((key) => {
                        toast({
                            title: violations[key],
                            status: "error",
                        });
                    });
                } else if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                    resetFormState();
                } else if (response !== null) {
                    toast({
                        title: t("cmn:message.save.success"),
                        status: "success",
                    });
                    if (selectedDate) {
                        fetchTimesheetData(selectedDate);
                    }
                    resetFormState();
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const deleteHandler = async () => {
        if (actionSelectId < 1) {
            return;
        }
        setLoading(true);
        await TimesheetApi.deleteItem(actionSelectId)
            .then(({ errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else {
                    if (selectedDate) {
                        fetchTimesheetData(selectedDate);
                    }
                }
            })
            .finally(() => setLoading(false));
    };

    const transformDate = (value: string | Date): dayjs.Dayjs => {
        return dayjs(value)
            .set("year", 1970)
            .set("month", 0)
            .set("date", 1)
            .set("second", 0)
            .set("millisecond", 0);
    };

    const validate = () => {
        const newErrors = {
            timesheetType: false,
            startTime: false,
            endTime: false,
        };
        const newErrorMessages: errorsMessageDataType = {};
        if (!selectedTimesheetType) {
            newErrors.timesheetType = true;
            newErrorMessages.timesheetType = t("cmn:val.required.short");
        }
        if (!selectedStartTime) {
            newErrors.startTime = true;
            newErrorMessages.startTime = t("cmn:val.required.short");
        }
        if (!selectedEndTime) {
            newErrors.endTime = true;
            newErrorMessages.endTime = t("cmn:val.required.short");
        }
        if (selectedStartTime && selectedEndTime) {
            const start = transformDate(selectedStartTime);
            const end = transformDate(selectedEndTime);

            if (!end.isAfter(start)) {
                newErrors.endTime = true;
                newErrorMessages.endTime = t(
                    "ent.Timesheet:endTime.val.greaterThanStart"
                );
            } else {
                const hasConflict = timesheet
                    .filter((row) => row.id !== editId)
                    .some((row) => {
                        const existingStart = dayjs(
                            (row.startTime as string).replace("+00:00", "")
                        );
                        const existingEnd = dayjs(
                            (row.endTime as string).replace("+00:00", "")
                        );
                        const overlap = !(
                            end <= existingStart || start >= existingEnd
                        );
                        return overlap;
                    });
                if (hasConflict) {
                    newErrors.startTime = true;
                    newErrors.endTime = true;
                    newErrorMessages.startTime = t(
                        "ent.Timesheet:startTime.val.conflictsWithOthers"
                    );
                    newErrorMessages.endTime = t(
                        "ent.Timesheet:endTime.val.conflictsWithOthers"
                    );
                }
            }
        }
        setErrors(newErrors);
        setErrorMessages(newErrorMessages);
        return !Object.values(newErrors).some((e) => e);
    };

    const resetFormState = () => {
        setEditId(0);
        setIsCreateEdit(false);
        setSelectedStartTime(null);
        setSelectedEndTime(null);
        setSelectedTimesheetType("");
        setSelectedComment("");
        setErrors({
            timesheetType: false,
            startTime: false,
            endTime: false,
        });
        setErrorMessages({});
    };

    const initializeRowForEdit = (row: Timesheet) => {
        setEditId(row.id);
        setIsCreateEdit(true);
        setSelectedTimesheetType(
            (row.timesheetType as TimesheetType)?.id?.toString() || ""
        );
        setSelectedStartTime(
            row.startTime
                ? dayjs(
                      (row.startTime as string).replace("+00:00", "")
                  ).toDate()
                : null
        );
        setSelectedEndTime(
            row.endTime
                ? dayjs((row.endTime as string).replace("+00:00", "")).toDate()
                : null
        );
        setSelectedComment(row.comment || "");
        setErrors({
            timesheetType: false,
            startTime: false,
            endTime: false,
        });
    };

    // useEffect
    useEffect(() => {
        if (!internshipScheduleSeatId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        fetchTimesheetTypeData();
        InternshipScheduleSeatApi.getItemMyInternship<InternshipScheduleSeat>(
            internshipScheduleSeatId
        )
            .then(({ errorMessage, isNotFound, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (isNotFound) {
                    toast({
                        title: t("cmn:message.entityNotFound"),
                        status: "error",
                    });
                } else if (response !== null) {
                    const is =
                        response.internshipSchedule as InternshipSchedule;

                    setData(is);
                    const startDate = dayjs(is?.startDate);
                    const endDate = dayjs(is?.endDate);
                    const now = new Date();
                    let sd = selectedDate || now;
                    if (now < startDate.toDate()) {
                        sd = startDate.toDate();
                    }
                    if (now > endDate.toDate()) {
                        sd = endDate.toDate();
                    }
                    setSelectedDate(sd);
                    setInternshipScheduleSeat(response);
                }
            })
            .finally(() => setLoading(false));
    }, [internshipScheduleSeatId]);

    useEffect(() => {
        if (selectedDate) {
            calculateDateSliderData(data);
            fetchTimesheetData(selectedDate);
        }
    }, [selectedDate]);

    // render
    const formRow = (rowData: Timesheet = new Timesheet()) => {
        return (
            <HStack
                key={rowData.id}
                gap={2}
                w={"900px"}
                alignItems={"flex-start"}
            >
                <Box flex="1">
                    <AppFormControl
                        isInvalid={errors.timesheetType}
                        message={errorMessages?.timesheetType}
                    >
                        <Select
                            placeholder={t(
                                "ent.Timesheet:timesheetType.placeholder"
                            )}
                            size="sm"
                            onChange={(e) => {
                                setSelectedTimesheetType(e.currentTarget.value);
                                setErrors((prev) => ({
                                    ...prev,
                                    timesheetType: false,
                                }));
                            }}
                            value={selectedTimesheetType}
                        >
                            {timesheetTypeOptions?.map((o) => (
                                <option value={o.value} key={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </Select>
                    </AppFormControl>
                </Box>
                <Box flex="1">
                    <AppFormControl
                        isInvalid={errors.startTime}
                        message={errorMessages?.startTime}
                    >
                        <AppDatePicker
                            selected={selectedStartTime ?? null}
                            onChange={(date) => {
                                setSelectedStartTime(date);
                                setErrors((prev) => ({
                                    ...prev,
                                    startTime: false,
                                }));
                            }}
                            placeholderText={t(
                                "ent.Timesheet:startTime.placeholder"
                            )}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption={t("cmn:label.time")}
                            dateFormat={APPC.APP_DP_TIME_FORMAT}
                            timeFormat={APPC.APP_DP_TIME_FORMAT}
                            customInput={<Input size={"sm"} />}
                            isClearable={true}
                        />
                    </AppFormControl>
                </Box>
                <Box flex="1">
                    <AppFormControl
                        isInvalid={errors.endTime}
                        message={errorMessages?.endTime}
                    >
                        <AppDatePicker
                            selected={selectedEndTime ?? null}
                            onChange={(date) => {
                                setSelectedEndTime(date);
                                setErrors((prev) => ({
                                    ...prev,
                                    endTime: false,
                                }));
                            }}
                            placeholderText={t(
                                "ent.Timesheet:endTime.placeholder"
                            )}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption={t("cmn:label.time")}
                            dateFormat={APPC.APP_DP_TIME_FORMAT}
                            timeFormat={APPC.APP_DP_TIME_FORMAT}
                            customInput={<Input size={"sm"} />}
                            isClearable={true}
                        />
                    </AppFormControl>
                </Box>
                <Box flex="2">
                    <Input
                        type="text"
                        placeholder={t("ent.Timesheet:comment.placeholder")}
                        size={"sm"}
                        defaultValue={rowData?.comment || ""}
                        onBlur={(e) => {
                            setSelectedComment(e.currentTarget.value);
                        }}
                    />
                </Box>
                <Box flex="1" textAlign={"end"}>
                    <HStack width={"full"} gap={4} justifyContent={"end"}>
                        <Button
                            variant={"unstyled"}
                            onClick={onTimesheetSubmitHandler}
                        >
                            <AppIcon name="icl-check-alt" w="1.25rem" />
                        </Button>
                        <Button variant={"unstyled"} onClick={resetFormState}>
                            <AppIcon name="icl-x" w="1.25rem" />
                        </Button>
                    </HStack>
                </Box>
            </HStack>
        );
    };

    const dataRow = (dataRow: Timesheet) => {
        return (
            <HStack gap={2} w={"900px"} key={dataRow.id}>
                <Box flex="1" textStyle={"regularXs"}>
                    {(dataRow.timesheetType as TimesheetType).name}
                </Box>
                <Box flex="1" textStyle={"regularXs"}>
                    {dayjs(
                        (dataRow.startTime as string).replace("+00:00", "")
                    ).format(APPC.APP_TIME_FORMAT)}
                </Box>
                <Box flex="1" textStyle={"regularXs"}>
                    {dayjs(
                        (dataRow.endTime as string).replace("+00:00", "")
                    ).format(APPC.APP_TIME_FORMAT)}
                </Box>
                <Box flex="2" textStyle={"regularXs"} noOfLines={1}>
                    {dataRow.comment}
                </Box>
                <Box flex="1" textAlign={"end"}>
                    <HStack width={"full"} gap={4} justifyContent={"end"}>
                        <Button
                            variant={"unstyled"}
                            disabled={isCreateEdit || dataRow.isValidated}
                            onClick={() => initializeRowForEdit(dataRow)}
                        >
                            <AppIcon name="icl-pencil" w="1.25rem" />
                        </Button>
                        <Button
                            variant={"unstyled"}
                            disabled={isCreateEdit || dataRow.isValidated}
                            onClick={() => {
                                setActionSelectId(dataRow.id);
                                deleteAlertState.onOpen();
                            }}
                        >
                            <AppIcon
                                name="icl-trash"
                                w="1.25rem"
                                color={"var(--chakra-colors-destructive)"}
                            />
                        </Button>
                    </HStack>
                </Box>
            </HStack>
        );
    };

    if (loading || !selectedDate) {
        <AppLoader />;
    }

    return (
        <>
            <AppPageHeader
                title={t("padm.TimesheetPage:text.pageTitle")}
                breadcrumb={
                    internshipScheduleSeat
                        ? [
                              {
                                  label: `${
                                      (
                                          (
                                              internshipScheduleSeat?.internshipSchedule as InternshipSchedule
                                          )?.internship as Internship
                                      ).name
                                  } (${
                                      (
                                          internshipScheduleSeat?.internshipSchedule as InternshipSchedule
                                      ).name
                                  })`,
                                  url: cPath("ADMIN.MY_INTERNSHIP_PAGE"),
                              },
                          ]
                        : []
                }
            ></AppPageHeader>
            <AppPageContent>
                <>
                    <VStack p={6} gap={6}>
                        <AppDateSelectSlider
                            data={dateSliderData}
                            selectedDate={selectedDate || new Date()}
                            setSelectedDate={setSelectedDate}
                            start={dayjs(data.startDate).toDate()}
                            end={dayjs(data.endDate).toDate()}
                        />
                    </VStack>
                    {!isSelectedDateDisabled && (
                        <VStack
                            p={6}
                            gap={2}
                            alignItems={"flex-start"}
                            overflow={"auto"}
                            h={"calc(100vh - 255px)"}
                        >
                            <HStack gap={2} w={"900px"}>
                                <Box flex="1" textStyle={"mediumSm"}>
                                    {t("ent.Timesheet:timesheetType.label")}
                                </Box>
                                <Box flex="1" textStyle={"mediumSm"}>
                                    {t("ent.Timesheet:startTime.label")}
                                </Box>
                                <Box flex="1" textStyle={"mediumSm"}>
                                    {t("ent.Timesheet:endTime.label")}
                                </Box>
                                <Box flex="2" textStyle={"mediumSm"}>
                                    {t("ent.Timesheet:comment.label")}
                                </Box>

                                <Box flex="1" textAlign={"end"}>
                                    <Button
                                        variant={"unstyled"}
                                        disabled={isCreateEdit}
                                        onClick={() => {
                                            setIsCreateEdit(true);
                                        }}
                                    >
                                        <AppIcon
                                            name="icl-plus-square"
                                            w="1.25rem"
                                            color={
                                                "var(--chakra-colors-primary)"
                                            }
                                        />
                                    </Button>
                                </Box>
                            </HStack>
                            <Divider />
                            {timesheet.map((t) => {
                                return isCreateEdit && editId === t.id
                                    ? formRow(t)
                                    : dataRow(t);
                            })}
                            {isCreateEdit && editId < 1 && formRow()}
                            {deleteAlertState.isOpen && (
                                <AppAlertDelete
                                    isOpenDelete={deleteAlertState.isOpen}
                                    deleteHandler={() => deleteHandler()}
                                    onCloseDelete={deleteAlertState.onClose}
                                />
                            )}
                        </VStack>
                    )}
                </>
            </AppPageContent>
        </>
    );
};
